import { EmbedMessage } from "@mm-sales/shared";
import { type ReactNode, createContext, useContext, useEffect, useState } from "react";

/**
 * Token lifecycle. Three ways in, in priority order:
 *  1. postMessage from the MyManager host page (production embed)
 *  2. ?token= in the URL (deep links; stripped after read)
 *  3. POST /dev/login (local dev, non-production api only)
 * Held in sessionStorage so a reload inside the iframe survives.
 */
const KEY = "mm-sales:token";
/** Set when the session token came from the local dev sign-in (the only standalone path, dev builds only). */
const DEV_KEY = "mm-sales:dev-login";
const RENEW_EVERY_MS = 10 * 60_000;
/**
 * Same-origin tabs can lend a token to a portal tab. A portal opened from a share
 * link or a plain new tab has no opener, but the rep's studio (embedded in
 * MyManager) is usually open in the same browser; it answers on this channel.
 * Only a portal tab accepts a token this way, and only an embedded studio lends.
 */
const CHANNEL = "mm-sales:tokens";
// Set once we bounced through the host's launcher, so a bad token can't loop us forever.
const HANDOFF_TRIED = "mm-sales:handoff-tried";
let current: string | null = sessionStorage.getItem(KEY);
export const getToken = () => current;
/** Last few handshake events, shown on the sign-in wait screen when it drags on. */
const debug: string[] = [];
export const authDebug = () => debug.slice(-6);
const note = (s: string) => {
  debug.push(`${new Date().toISOString().slice(11, 19)} ${s}`);
  if (debug.length > 30) debug.shift();
};
export const setToken = (t: string | null) => {
  current = t;
  if (t) {
    sessionStorage.setItem(KEY, t);
    sessionStorage.removeItem(HANDOFF_TRIED);
  } else sessionStorage.removeItem(KEY);
};

const hostWindow = (): Window | null =>
  window.parent !== window ? window.parent : (window.opener as Window | null);

/**
 * The prospect portal (/p/<token>) is the one screen a rep opens in its own tab.
 * "Launch" normally opens it with an opener, but a popup blocker turns that into a
 * plain tab. Such a tab is still recognisable: it came from the studio itself
 * (same-origin referrer under /app). Only the portal gets this allowance, and only
 * for the short-lived token in the Launch URL.
 */
const portalTab = () => window.location.pathname.startsWith("/p/");
/** Window name "Launch" gives the portal window (see api.ts openDemoWindow); a typed URL has none. */
export const launchWindowName = (shareToken: string) => `mm-sales-demo-${shareToken}`;
const launchedFromStudio = () => {
  const share = window.location.pathname.split("/")[2] ?? "";
  if (share && window.name === launchWindowName(share)) return true;
  try {
    const r = new URL(document.referrer);
    return r.origin === window.location.origin && r.pathname.startsWith("/app");
  } catch {
    return false;
  }
};

/**
 * Is there a MyManager window to talk to? The studio only runs inside MyManager:
 * as an iframe (parent) or a window MyManager's embed opened (opener). Without a
 * host there is no one to mint or renew a token, so nothing signs in.
 */
const isHosted = () => Boolean(hostWindow());

const AuthCtx = createContext<{
  token: string | null;
  ready: boolean;
  /** Inside MyManager (iframe or a popup it opened). Standalone tabs get no session. */
  hosted: boolean;
  signOut: () => void;
  devLogin: () => Promise<void>;
  /** The api refused the token (401): drop it and, when embedded, ask the host for a fresh one. */
  rejectToken: () => void;
}>({
  token: null,
  ready: false,
  hosted: false,
  signOut: () => {},
  devLogin: async () => {},
  rejectToken: () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [hosted] = useState(isHosted);
  // A stored token only counts inside MyManager (or after the dev sign-in in a
  // dev build): a bare tab must not resume a session someone left behind.
  const [token, setTok] = useState<string | null>(() => {
    // A portal tab keeps the token Launch gave it across a reload (it expires on its own).
    if (
      current &&
      !(isHosted() || portalTab() || (import.meta.env.DEV && sessionStorage.getItem(DEV_KEY)))
    ) {
      note("session token ignored: not inside MyManager");
      setToken(null);
    }
    return current;
  });
  useEffect(() => {
    if (current) note("session token present at load");
  }, []);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const url = new URL(window.location.href);
    const fromUrl = url.searchParams.get("token");
    if (fromUrl) {
      // Only a window MyManager's embed opened (Launch) may carry a token in the URL —
      // or the portal tab that Launch fell back to when the popup was blocked.
      if (isHosted() || (portalTab() && launchedFromStudio())) {
        note("token from url");
        setToken(fromUrl);
        setTok(fromUrl);
      } else note("url token ignored: not inside MyManager");
      url.searchParams.delete("token");
      window.history.replaceState({}, "", url.toString());
    }
    // "Ready" flips once we know whether a host token is coming. With a host and
    // no token yet we hold the app on the loader until the reply arrives (or a
    // short timeout passes) instead of bouncing to the sign-in page first.
    let settled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const settle = () => {
      if (settled) return;
      settled = true;
      if (timer) clearTimeout(timer);
      setReady(true);
    };
    const onMessage = (e: MessageEvent) => {
      const parsed = EmbedMessage.safeParse(e.data);
      if (!parsed.success) return;
      // A window we opened (Launch popup, same origin) asks for a token: lend ours.
      // It has no MyManager parent of its own, only us as its opener.
      if (parsed.data.type === "mm-sales:ready") {
        if (
          e.origin === window.location.origin &&
          e.source &&
          e.source !== hostWindow() &&
          current
        ) {
          (e.source as Window).postMessage({ type: "mm-sales:token", token: current }, e.origin);
          note("lent token to a popup");
        }
        return;
      }
      if (parsed.data.type === "mm-sales:token") {
        note(
          `token message from ${e.origin}: ${parsed.data.token ? `len ${parsed.data.token.length}` : `null (${(parsed.data as { error?: string }).error ?? "no error"})`}`,
        );
        setToken(parsed.data.token);
        setTok(parsed.data.token);
        settle();
        // A late or re-requested token (after a 401) also un-holds the app.
        setReady(true);
      }
    };
    window.addEventListener("message", onMessage);
    const channel = typeof BroadcastChannel !== "undefined" ? new BroadcastChannel(CHANNEL) : null;
    if (channel) {
      channel.onmessage = (e: MessageEvent) => {
        const parsed = EmbedMessage.safeParse(e.data);
        if (!parsed.success) return;
        if (parsed.data.type === "mm-sales:ready") {
          const mine = sessionStorage.getItem(KEY);
          if (isHosted() && !portalTab() && mine) {
            channel.postMessage({ type: "mm-sales:token", token: mine });
            note("lent token to a portal tab (broadcast)");
          }
          return;
        }
        if (parsed.data.type === "mm-sales:token" && portalTab() && parsed.data.token) {
          note("token from a studio tab (broadcast)");
          setToken(parsed.data.token);
          setTok(parsed.data.token);
          settle();
          setReady(true);
        }
      };
    }
    // Tell the host we're listening; it replies with the token. Two hosts:
    // an embedding iframe (parent) or the MyManager tab that opened us in a
    // new tab (opener). A refresh keeps the sessionStorage token, so a tab
    // with no host left is still fine.
    // Embedded: ALWAYS ask the host for a fresh token, even when the session
    // still holds one. A stale session token (older secret, expired, revoked)
    // used to win here, get a 401, and loop between /app and /signin forever.
    const host = hostWindow();
    let waitingForHost = false;
    if (host) {
      try {
        host.postMessage({ type: "mm-sales:ready" }, "*");
        note(
          `posted ready to ${window.parent !== window ? "parent" : "opener"}${current ? " (session token kept as fallback)" : ""}`,
        );
        if (!current) {
          waitingForHost = true;
          timer = setTimeout(() => {
            note("no token within 5s — continuing without");
            settle();
          }, 5000);
        }
      } catch {
        /* opener gone */
      }
    }
    // A portal tab with no opener asks the other tabs; give them a moment to answer.
    const asksTabs = Boolean(channel && portalTab() && !host);
    if (asksTabs) {
      channel!.postMessage({ type: "mm-sales:ready" });
      if (!current && !fromUrl && !waitingForHost) {
        waitingForHost = true;
        timer = setTimeout(() => {
          note("no token from other tabs within 2.5s — continuing as the prospect view");
          settle();
        }, 2500);
      }
    }
    if (!waitingForHost) settle();
    // Tokens are short-lived: while hosted, ask for a fresh one well before it
    // expires, and again whenever the tab comes back into view.
    let renew: ReturnType<typeof setInterval> | null = null;
    const askAgain = () => {
      const h = hostWindow();
      if (!h) {
        if (asksTabs) channel!.postMessage({ type: "mm-sales:ready" });
        return;
      }
      try {
        h.postMessage({ type: "mm-sales:ready" }, "*");
        note("asked the host to renew the token");
      } catch {
        /* host gone */
      }
    };
    let lastAsk = Date.now();
    const onVisible = () => {
      if (document.visibilityState === "visible" && Date.now() - lastAsk > RENEW_EVERY_MS) {
        lastAsk = Date.now();
        askAgain();
      }
    };
    if (host || asksTabs) {
      renew = setInterval(() => {
        lastAsk = Date.now();
        askAgain();
      }, RENEW_EVERY_MS);
      document.addEventListener("visibilitychange", onVisible);
    }
    return () => {
      window.removeEventListener("message", onMessage);
      document.removeEventListener("visibilitychange", onVisible);
      if (timer) clearTimeout(timer);
      if (renew) clearInterval(renew);
      channel?.close();
    };
  }, []);

  const signOut = () => {
    setToken(null);
    setTok(null);
  };
  const rejectToken = () => {
    note("api rejected the token (401) — dropping it");
    setToken(null);
    setTok(null);
    const host = hostWindow();
    if (!host) {
      // A portal tab without an opener: ask the studio tab (if any) for a fresh one.
      if (portalTab() && typeof BroadcastChannel !== "undefined") {
        const c = new BroadcastChannel(CHANNEL);
        c.postMessage({ type: "mm-sales:ready" });
        setTimeout(() => c.close(), 1000);
        note("asked other tabs for a fresh token");
      }
      return;
    }
    try {
      setReady(false);
      host.postMessage({ type: "mm-sales:ready" }, "*");
      note("asked the host for a fresh token");
      setTimeout(() => setReady(true), 5000);
    } catch {
      setReady(true);
    }
  };
  const devLogin = async () => {
    const res = await fetch("/dev/login", { method: "POST" });
    if (!res.ok) throw new Error("dev login is only available on a non-production api");
    const data = (await res.json()) as { token: string };
    setToken(data.token);
    setTok(data.token);
    sessionStorage.setItem(DEV_KEY, "1");
  };

  return (
    <AuthCtx.Provider value={{ token, ready, hosted, signOut, devLogin, rejectToken }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
